import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query"
import { AxiosResponse } from "axios"
import { RemesaDtoProps } from "./DTOs/RemesaDto"
import { RemesaFlujoTesoreriaDtoProps } from "./DTOs/RemesaFlujoTesoreriaDto"
import { RemesaReadingDtoProps } from "./DTOs/RemesaReadingDto"
import {
  getRemesaById,
  getRemesaFlujoTesoreriaById,
  updateRemesaById,
} from "./Remesa.service"

const remesaQueryKeys = {
  all: ["remesas"],
  byId: (id: string) => ["remesas", "by-id", id] as const,
} as const

type KeyById = ReturnType<typeof remesaQueryKeys.byId>

type RemesaByIdOptions<TSelected> = Omit<
  UseQueryOptions<RemesaReadingDtoProps, Error, TSelected, KeyById>,
  "queryKey" | "queryFn"
>

/**
 * Hook para obtener una Remesa por ID.
 *
 * @param id - Identificador de la Remesa
 * @param options - Opciones adicionales de configuración para el query
 */
export function useRemesaById<TSelected = RemesaReadingDtoProps>(
  id: string,
  options?: RemesaByIdOptions<TSelected>
): UseQueryResult<TSelected, Error> {
  return useQuery({
    queryKey: remesaQueryKeys.byId(id),
    queryFn: () => getRemesaById(id),
    enabled: Boolean(id),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnMount: false,
    ...options,
  })
}

const remesaFlujoTesoreriaQueryKeys = {
  all: ["remesas-flujos-tesoreria"],
  byId: (id: string) => ["remesas-flujos-tesoreria", "by-id", id] as const,
} as const
type RemesaFlujoTesoreriaByIdKey = ReturnType<
  typeof remesaFlujoTesoreriaQueryKeys.byId
>
type RemesaFlujoTesoreriaByIdOptions<TSelected> = Omit<
  UseQueryOptions<
    Array<RemesaFlujoTesoreriaDtoProps>,
    Error,
    TSelected,
    RemesaFlujoTesoreriaByIdKey
  >,
  "queryKey" | "queryFn"
>

export function useRemesaFlujoTesoreriaById<
  TSelected = Array<RemesaFlujoTesoreriaDtoProps>
>(
  id: string,
  options?: RemesaFlujoTesoreriaByIdOptions<TSelected>
): UseQueryResult<TSelected, Error> {
  return useQuery({
    queryKey: remesaFlujoTesoreriaQueryKeys.byId(id),
    queryFn: () => getRemesaFlujoTesoreriaById(id),
    enabled: Boolean(id),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnMount: false,
    ...options,
  })
}

type Variables = {
  id: string
  remesa: RemesaDtoProps
}

export function useUpdateRemesaById(
  options?: UseMutationOptions<AxiosResponse, Error, Variables>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, remesa }: Variables) => updateRemesaById(id, remesa),
    onSuccess: (_response, { id }) => {
      queryClient.invalidateQueries({ queryKey: remesaQueryKeys.byId(id) })
    },

    ...options,
  })
}
